/**
 * Top Navigation Bar Component
 * Coordinates mobile menus, dark/light toggle switches, global search suggestion menus, and user profiles.
 */

import { auth } from '../firebase.js';
import { logoutUser } from '../auth.js';
import { showToast } from '../utils.js';
import { listenToDoctors } from '../models/doctorModel.js';
import { getProducts } from '../models/productModel.js';

let globalDoctors = [];
let globalProducts = [];
let unsubscribeDoctorsGlobal = null;

// Generate active dataset dynamically merging Firestore records and static page routes
const getActiveDataset = () => {
  const dataset = [];

  // 1. Add doctors
  globalDoctors.forEach(doc => {
    dataset.push({
      type: 'doctor',
      text: `${doc.name} (${doc.specialization || 'General Medicine'})`,
      route: 'doctors'
    });
  });

  // 2. Add medicines/products
  globalProducts.forEach(prod => {
    dataset.push({
      type: 'medicine',
      text: prod.name,
      route: 'sales'
    });
  });

  // 3. Add static pages/reports
  dataset.push(
    { type: 'report', text: 'Q2 Regional Sales Analytics', route: 'reports' },
    { type: 'report', text: 'Doctor Visit Coverage Summary', route: 'reports' },
    { type: 'page', text: 'Financial Investment Panel', route: 'investment' },
    { type: 'page', text: 'Account Configurations', route: 'settings' }
  );

  return dataset;
};


/**
 * Render Topbar HTML layout
 */
export function renderTopbar() {
  const container = document.getElementById('topbar-container');
  if (!container) return;

  const currentUser = auth.currentUser;
  const userInitial = currentUser && currentUser.email ? currentUser.email.charAt(0).toUpperCase() : 'U';
  const userEmail = currentUser && currentUser.email ? currentUser.email : 'user@aurapharma.com';
  const photoURL = currentUser && currentUser.photoURL ? currentUser.photoURL : '';

  container.innerHTML = `
    <!-- Left Section: Toggle & Breadcrumbs -->
    <div class="d-flex align-items-center gap-3">
      <button id="sidebar-toggle" class="sidebar-toggle-btn" aria-label="Toggle Sidebar">
        <i class="bi bi-list"></i>
      </button>
      
      <nav aria-label="breadcrumb" class="d-none d-md-inline-block">
        <ol class="breadcrumb mb-0">
          <li class="breadcrumb-item text-muted"><span class="small text-uppercase">Urena Healthcare</span></li>
          <li class="breadcrumb-item active text-primary fw-semibold" id="topbar-breadcrumb-active" aria-current="page">Dashboard</li>
        </ol>
      </nav>
    </div>

    <!-- Center Section: Global Search Component -->
    <div class="global-search-container">
      <div class="search-input-wrapper">
        <i class="bi bi-search"></i>
        <input type="text" id="global-search-input" class="form-control form-control-sm border-0 bg-light" placeholder="Search doctors, medicines, reports..." autocomplete="off">
      </div>
      
      <!-- Suggestions dropdown dropdown panel -->
      <div id="search-suggestions" class="search-suggestions-panel">
        <!-- populated dynamically -->
      </div>
    </div>

    <!-- Right Section: Theme Toggle, Notifications, Profile Dropdown -->
    <div class="d-flex align-items-center gap-3">
      
      <!-- Dark/Light Theme Button -->
      <button id="theme-toggle" class="btn btn-icon sidebar-toggle-btn p-2 border-0" aria-label="Toggle Theme">
        <i class="bi bi-moon-fill" id="theme-icon"></i>
      </button>

      <!-- Notifications Dropdown -->
      <div class="dropdown">
        <button class="sidebar-toggle-btn position-relative" type="button" id="notif-dropdown" data-bs-toggle="dropdown" aria-expanded="false">
          <i class="bi bi-bell"></i>
          <span id="notif-badge" class="position-absolute top-1 start-100 translate-middle p-1 bg-danger border border-light rounded-circle d-none">
            <span class="visually-hidden">New alerts</span>
          </span>
        </button>
        
        <ul class="dropdown-menu dropdown-menu-end dropdown-menu-custom" aria-labelledby="notif-dropdown" style="width: 280px;">
          <li class="dropdown-header font-heading fw-bold pb-2 border-bottom border-color">Notifications</li>
          <div id="notif-items">
            <!-- Dynamic alerts go here -->
          </div>
          <li><hr class="dropdown-divider"></li>
          <li><a class="dropdown-item text-center text-primary fw-semibold small py-1" href="#reports">View all reports</a></li>
        </ul>
      </div>

      <!-- User Profile Dropdown -->
      <div class="dropdown">
        <button class="btn btn-link p-0 border-0 d-flex align-items-center text-decoration-none dropdown-toggle-no-caret" type="button" id="profile-dropdown" data-bs-toggle="dropdown" aria-expanded="false">
          <div class="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center fw-bold overflow-hidden" style="width: 38px; height: 38px; font-size: 0.95rem; box-shadow: var(--shadow-sm);">
            ${photoURL 
              ? `<img src="${photoURL}" alt="Avatar" style="width: 100%; height: 100%; object-fit: cover;">` 
              : userInitial
            }
          </div>
        </button>
        
        <ul class="dropdown-menu dropdown-menu-end dropdown-menu-custom" aria-labelledby="profile-dropdown" style="width: 220px;">
          <li class="px-3 py-2 border-bottom border-color mb-1">
            <div class="fw-bold font-heading text-truncate" style="max-width: 180px;">User Account</div>
            <div class="text-muted text-truncate" style="max-width: 180px; font-size: 0.75rem;">${userEmail}</div>
          </li>
          <li><a class="dropdown-item" href="#profile"><i class="bi bi-person-fill me-2"></i>My Profile</a></li>
          <li><a class="dropdown-item" href="#settings"><i class="bi bi-gear-fill me-2"></i>Settings</a></li>
          <li><hr class="dropdown-divider"></li>
          <li>
            <button id="topbar-logout-btn" class="dropdown-item text-danger d-flex align-items-center bg-transparent border-0 w-100">
              <i class="bi bi-box-arrow-right me-2"></i>Log Out
            </button>
          </li>
        </ul>
      </div>

    </div>
  `;
}

/**
 * Initialize Event Listeners for the Topbar
 */
export function initTopbar() {
  const sidebarToggle = document.getElementById('sidebar-toggle');
  const themeToggle = document.getElementById('theme-toggle');
  const themeIcon = document.getElementById('theme-icon');
  const searchInput = document.getElementById('global-search-input');
  const suggestionsPanel = document.getElementById('search-suggestions');
  const logoutBtn = document.getElementById('topbar-logout-btn');

  // 1. Sidebar Collapse/Open controls
  if (sidebarToggle) {
    sidebarToggle.addEventListener('click', () => {
      if (window.innerWidth < 992) {
        document.body.classList.toggle('sidebar-open');
        const backdrop = document.querySelector('.sidebar-backdrop');
        if (backdrop) {
          backdrop.classList.toggle('d-none');
        }
      } else {
        document.body.classList.toggle('sidebar-collapsed');
      }
    });
  }

  // 2. Dark/Light Theme toggler
  if (themeToggle && themeIcon) {
    // Set initial icon match
    updateThemeIcon(document.documentElement.getAttribute('data-theme'), themeIcon);

    themeToggle.addEventListener('click', () => {
      const currentTheme = document.documentElement.getAttribute('data-theme');
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      
      document.documentElement.setAttribute('data-theme', newTheme);
      localStorage.setItem('pharma-crm-theme', newTheme);
      updateThemeIcon(newTheme, themeIcon);
      showToast("Theme Updated", `Switched to ${newTheme} mode.`, "info");
    });
  }

  // 3. User logout button
  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      try {
        await logoutUser();
        showToast("Logged Out", "You have successfully signed out.", "info");
      } catch (error) {
        showToast("Error", "Could not complete logout.", "error");
      }
    });
  }

  // Subscribe to dynamic doctors list from Firestore
  if (!unsubscribeDoctorsGlobal) {
    unsubscribeDoctorsGlobal = listenToDoctors((doctors) => {
      globalDoctors = doctors;
      updateNotifications();
    });
  }

  // Load products list once
  if (globalProducts.length === 0) {
    getProducts().then((products) => {
      globalProducts = products;
    }).catch((err) => {
      console.error("Failed to load products for global search:", err);
    });
  }

  // 4. Global Search query and suggestions popup
  if (searchInput && suggestionsPanel) {
    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase().trim();
      if (!query) {
        suggestionsPanel.innerHTML = '';
        suggestionsPanel.classList.remove('show');
        return;
      }

      // Filter matches dynamically
      const matches = getActiveDataset().filter(item => item.text.toLowerCase().includes(query));
      
      if (matches.length === 0) {
        suggestionsPanel.innerHTML = `<div class="p-3 text-muted text-center small">No records match "${query}"</div>`;
        suggestionsPanel.classList.add('show');
        return;
      }

      // Format matching list
      const groups = { doctor: [], medicine: [], report: [], page: [] };
      matches.forEach(item => {
        groups[item.type].push(item);
      });

      let panelContent = '';
      
      if (groups.doctor.length > 0) {
        panelContent += `<div class="suggestion-group-title">Doctors</div>`;
        groups.doctor.forEach(item => {
          const cleanSearchTerm = item.text.split('(')[0].trim();
          panelContent += `
            <a href="#${item.route}?search=${encodeURIComponent(cleanSearchTerm)}" class="suggestion-item text-decoration-none" data-search-item>
              <i class="bi bi-people-fill"></i>
              <span>${item.text}</span>
            </a>
          `;
        });
      }

      if (groups.medicine.length > 0) {
        panelContent += `<div class="suggestion-group-title">Medicines</div>`;
        groups.medicine.forEach(item => {
          const cleanSearchTerm = item.text.split('(')[0].trim();
          panelContent += `
            <a href="#${item.route}?search=${encodeURIComponent(cleanSearchTerm)}" class="suggestion-item text-decoration-none" data-search-item>
              <i class="bi bi-capsule"></i>
              <span>${item.text}</span>
            </a>
          `;
        });
      }

      if (groups.report.length > 0) {
        panelContent += `<div class="suggestion-group-title">Reports</div>`;
        groups.report.forEach(item => {
          const cleanSearchTerm = item.text.split('(')[0].trim();
          panelContent += `
            <a href="#${item.route}?search=${encodeURIComponent(cleanSearchTerm)}" class="suggestion-item text-decoration-none" data-search-item>
              <i class="bi bi-bar-chart-line-fill"></i>
              <span>${item.text}</span>
            </a>
          `;
        });
      }

      if (groups.page.length > 0) {
        panelContent += `<div class="suggestion-group-title">Pages</div>`;
        groups.page.forEach(item => {
          const cleanSearchTerm = item.text.split('(')[0].trim();
          panelContent += `
            <a href="#${item.route}?search=${encodeURIComponent(cleanSearchTerm)}" class="suggestion-item text-decoration-none" data-search-item>
              <i class="bi bi-link-45deg"></i>
              <span>${item.text}</span>
            </a>
          `;
        });
      }

      suggestionsPanel.innerHTML = panelContent;
      suggestionsPanel.classList.add('show');

      // Bind link click triggers inside suggestions to automatically clear the search
      suggestionsPanel.querySelectorAll('[data-search-item]').forEach(el => {
        el.addEventListener('click', () => {
          searchInput.value = '';
          suggestionsPanel.classList.remove('show');
        });
      });
    });

    // Execute global search when pressing Enter or clicking search icon
    const executeGlobalSearch = (queryText) => {
      const query = queryText.toLowerCase().trim();
      if (!query) return;

      const activeData = getActiveDataset();
      const matches = activeData.filter(item => item.text.toLowerCase().includes(query));
      if (matches.length > 0) {
        const firstMatch = matches[0];
        const cleanSearchTerm = firstMatch.text.split('(')[0].trim();
        window.location.hash = `#${firstMatch.route}?search=${encodeURIComponent(cleanSearchTerm)}`;
      } else {
        // Fallback: If query matches/contains a known medicine or common keywords, search sales, otherwise doctors
        const isMed = globalProducts.some(p => p.name.toLowerCase().includes(query)) || 
                      ['tab', 'cap', 'mg', 'ml', 'medicine', 'drug', 'bonura', 'etoura', 'tenjo', 'urac'].some(k => query.includes(k));
        
        if (isMed) {
          window.location.hash = `#sales?search=${encodeURIComponent(queryText.trim())}`;
        } else {
          window.location.hash = `#doctors?search=${encodeURIComponent(queryText.trim())}`;
        }
      }

      searchInput.value = '';
      suggestionsPanel.classList.remove('show');
    };

    // Allow clicking the search icon to trigger search execution
    const searchIcon = searchInput.previousElementSibling;
    if (searchIcon) {
      searchIcon.style.cursor = 'pointer';
      searchIcon.addEventListener('click', () => {
        executeGlobalSearch(searchInput.value);
      });
    }

    // Allow pressing Enter key to trigger search execution
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        executeGlobalSearch(searchInput.value);
      }
    });

    // Close suggestions box when user clicks outside
    document.addEventListener('click', (event) => {
      if (!searchInput.contains(event.target) && !suggestionsPanel.contains(event.target)) {
        suggestionsPanel.classList.remove('show');
      }
    });
  }
}

/**
 * Update the dynamic topbar notifications list based on upcoming events
 */
function updateNotifications() {
  const notifItemsContainer = document.getElementById('notif-items');
  const notifBadge = document.getElementById('notif-badge');
  if (!notifItemsContainer) return;

  const notifications = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  globalDoctors.forEach(doctor => {
    // Birthday Alert
    if (doctor.dob) {
      const dobDate = new Date(doctor.dob);
      if (!isNaN(dobDate.getTime())) {
        const birthdayThisYear = new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate());
        
        let diffTime = birthdayThisYear.getTime() - today.getTime();
        let diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        // Wrap year boundaries if birthday is in Dec/Jan
        if (diffDays < -300) {
          birthdayThisYear.setFullYear(today.getFullYear() + 1);
          diffTime = birthdayThisYear.getTime() - today.getTime();
          diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        } else if (diffDays > 300) {
          birthdayThisYear.setFullYear(today.getFullYear() - 1);
          diffTime = birthdayThisYear.getTime() - today.getTime();
          diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        }

        // Show alert if the event is in 0, 1, or 2 days
        if (diffDays >= 0 && diffDays <= 2) {
          let message = '';
          if (diffDays === 0) message = `Dr. ${doctor.name}'s Birthday is today! 🎂`;
          else if (diffDays === 1) message = `Dr. ${doctor.name}'s Birthday is tomorrow! 🎉`;
          else message = `Dr. ${doctor.name}'s Birthday is in 2 days (on ${birthdayThisYear.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}).`;

          notifications.push({
            type: 'birthday',
            title: 'Upcoming Birthday',
            message: message,
            link: `#doctors?id=${doctor.id}`,
            iconClass: 'text-danger bi-cake2-fill'
          });
        }
      }
    }

    // Wedding Anniversary Alert
    if (doctor.anniversary) {
      const annivDate = new Date(doctor.anniversary);
      if (!isNaN(annivDate.getTime())) {
        const anniversaryThisYear = new Date(today.getFullYear(), annivDate.getMonth(), annivDate.getDate());
        
        let diffTime = anniversaryThisYear.getTime() - today.getTime();
        let diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        // Wrap year boundaries if anniversary is in Dec/Jan
        if (diffDays < -300) {
          anniversaryThisYear.setFullYear(today.getFullYear() + 1);
          diffTime = anniversaryThisYear.getTime() - today.getTime();
          diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        } else if (diffDays > 300) {
          anniversaryThisYear.setFullYear(today.getFullYear() - 1);
          diffTime = anniversaryThisYear.getTime() - today.getTime();
          diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        }

        // Show alert if the event is in 0, 1, or 2 days
        if (diffDays >= 0 && diffDays <= 2) {
          let message = '';
          if (diffDays === 0) message = `Dr. ${doctor.name}'s Anniversary is today! 💍`;
          else if (diffDays === 1) message = `Dr. ${doctor.name}'s Anniversary is tomorrow! 🥂`;
          else message = `Dr. ${doctor.name}'s Anniversary is in 2 days (on ${anniversaryThisYear.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}).`;

          notifications.push({
            type: 'anniversary',
            title: 'Upcoming Wedding Anniversary',
            message: message,
            link: `#doctors?id=${doctor.id}`,
            iconClass: 'text-warning bi-heart-fill'
          });
        }
      }
    }
  });

  // Render notifications to topbar UI
  if (notifications.length > 0) {
    if (notifBadge) notifBadge.classList.remove('d-none');
    
    let html = '';
    notifications.forEach(n => {
      html += `
        <li>
          <a class="dropdown-item py-2" href="${n.link}">
            <div class="fw-semibold small d-flex align-items-center gap-1">
              <i class="bi ${n.iconClass}"></i>
              <span>${n.title}</span>
            </div>
            <div class="text-muted small" style="white-space: normal;">${n.message}</div>
          </a>
        </li>
      `;
    });
    notifItemsContainer.innerHTML = html;
  } else {
    if (notifBadge) notifBadge.classList.add('d-none');
    notifItemsContainer.innerHTML = `
      <li class="px-3 py-3 text-muted text-center small">
        <i class="bi bi-bell-slash d-block fs-4 mb-1"></i>
        No upcoming events in next 2 days
      </li>
    `;
  }
}

/**
 * Update Icon based on Active Theme mode
 * @param {string} theme 
 * @param {HTMLElement} iconElement 
 */
function updateThemeIcon(theme, iconElement) {
  if (theme === 'dark') {
    iconElement.className = 'bi bi-sun-fill text-warning';
  } else {
    iconElement.className = 'bi bi-moon-fill text-secondary';
  }
}

/**
 * Cleanup global topbar subscriptions (called on logout)
 */
export function destroyTopbar() {
  if (unsubscribeDoctorsGlobal) {
    unsubscribeDoctorsGlobal();
    unsubscribeDoctorsGlobal = null;
  }
}
